import { AxiosInstance } from 'axios'
import {
    Task,
    ListTasksParams,
    ListTasksResponse,
    CreateTaskBody,
    SubmitOverdueReasonBody,
    TaskActivity,
} from './task.type'
import { FolderTreeResponse } from '../folder/folder.type'

export class TaskApi {
    constructor(private readonly client: AxiosInstance) {}

    /**
     * Get all tasks with pagination and filters
     */
    async list(params?: ListTasksParams): Promise<ListTasksResponse> {
        const response = await this.client.get<ListTasksResponse>('/tasks', {
            params,
        })
        return response.data
    }

    /**
     * Get a single task by ID
     */
    async findOne(id: string): Promise<Task> {
        const response = await this.client.get<Task>(`/tasks/${id}`)
        return response.data
    }

    /**
     * Create a new task
     */
    async create(payload: CreateTaskBody | FormData): Promise<Task> {
        const response = await this.client.post<Task>('/tasks', payload)
        return response.data
    }

    /**
     * Update a task
     */
    async update(id: string, payload: Partial<Task> | FormData): Promise<Task> {
        const response = await this.client.put<Task>(`/tasks/${id}`, payload)
        return response.data
    }

    /**
     * Delete a task
     */
    async delete(id: string): Promise<void> {
        await this.client.delete(`/tasks/${id}`)
    }

    /**
     * Bulk delete tasks
     */
    async bulkDelete(ids: string[]): Promise<void> {
        await this.client.delete('/tasks', { data: { ids } })
    }

    /**
     * Get task activities
     */
    async getActivities(
        taskId: string,
        params?: { page?: number; take?: number },
    ): Promise<{ data: TaskActivity[]; meta: any }> {
        const response = await this.client.get<{ data: TaskActivity[]; meta: any }>(`/tasks/${taskId}/activities`, {
            params,
        })
        return response.data
    }

    /**
     * Get project information for a task (task-scoped project endpoint)
     */
    async getProject(taskId: string): Promise<any> {
        const response = await this.client.get(`/tasks/${taskId}/project`)
        return response.data
    }

    /**
     * Submit overdue explanation
     */
    async submitOverdueReason(taskId: string, taskOverdueId: string, body: SubmitOverdueReasonBody): Promise<void> {
        await this.client.put(`/tasks/${taskId}/overdue/${taskOverdueId}`, body)
    }

    /**
     * Create a comment on a task
     */
    async createComment(taskId: string, payload: FormData): Promise<void> {
        await this.client.post(`/tasks/${taskId}/comments`, payload)
    }

    /**
     * Accept a task
     */
    async accept(id: string): Promise<void> {
        await this.client.post(`/tasks/${id}/accept`)
    }

    /**
     * Return a task
     */
    async returnTask(id: string): Promise<void> {
        await this.client.post(`/tasks/${id}/return`)
    }

    /**
     * Complete a task
     */
    async complete(id: string): Promise<void> {
        await this.client.post(`/tasks/${id}/completed`)
    }

    /**
     * Request approval for a task
     */
    async requestApproval(taskId: string): Promise<void> {
        await this.client.post(`/tasks/${taskId}/request-approval`)
    }

    async getFolderTree(id: string): Promise<FolderTreeResponse> {
        const response = await this.client.get<FolderTreeResponse>(`/tasks/${id}/tree-folders`)
        return response.data
    }

    /**
     * Get users related to a task
     */
    async listUsers(taskId: string, params?: { page?: number; take?: number }) {
        const response = await this.client.get(`/tasks/${taskId}/users`, {
            params,
        })
        return response.data
    }
}
